import { useTranslation } from "react-i18next";
import { Button } from "@core/button";
import { Text } from "@core/text";
import {
  CheckCircle,
  ArrowRight,
  Sparkles,
  Heart,
  Clock,
  Star,
  BookOpen,
  Target,
  Users,
  Brain,
  Award,
  FileText,
  CheckSquare,
  AlertCircle,
} from "lucide-react";
import { useState } from "react";
import {
  UbdMaarifPlannerResponseDtoCourseType,
  LearningOutcomeDto,
} from "@packages/api/madlenAPI.schemas";

interface UbdPlanData {
  courseType: UbdMaarifPlannerResponseDtoCourseType | "";
  grade: number;
  learningOutcomes: LearningOutcomeDto[];
  ubdUnderstandingGoals: string[];
  ubdTransferGoals: string[];
  ubdEssentialQuestions: string[];
  ubdPerformanceTasks: string[];
  ubdOtherEvidence: string[];
  planId?: number;
}

interface UbdPlanSummaryStepProps {
  formData: UbdPlanData;
  isLoading: boolean;
  onCreateLessonFlow: () => void;
}

const UbdPlanSummaryStep: React.FC<UbdPlanSummaryStepProps> = ({
  formData,
  isLoading,
  onCreateLessonFlow,
}) => {
  const { t } = useTranslation();

  // Calculate completion for validation
  const getCompletionPercentage = () => {
    let completed = 0;
    let total = 7;

    if (formData.courseType) completed++;
    if (formData.grade > 0) completed++;
    if ((formData.learningOutcomes?.length ?? 0) > 0) completed++;
    if ((formData.ubdUnderstandingGoals?.length ?? 0) > 0) completed++;
    if ((formData.ubdTransferGoals?.length ?? 0) > 0) completed++;
    if ((formData.ubdEssentialQuestions?.length ?? 0) > 0) completed++;
    if (
      (formData.ubdPerformanceTasks?.length ?? 0) +
        (formData.ubdOtherEvidence?.length ?? 0) >
      0
    )
      completed++;

    return Math.round((completed / total) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  const getCourseTypeDisplay = (courseType: string) => {
    const courseTypes: { [key: string]: string } = {
      TURKCE: "Türkçe",
      MATEMATIK: "Matematik",
      FEN_BILIMLERI: "Fen Bilimleri",
      SOSYAL_BILGILER: "Sosyal Bilgiler",
      INGILIZCE: "İngilizce",
      TARIH: "Tarih",
      COGRAFYA: "Coğrafya",
      FIZIK: "Fizik",
      KIMYA: "Kimya",
      BIYOLOJI: "Biyoloji",
      EDEBIYAT: "Edebiyat",
      FELSEFE: "Felsefe",
      SANAT: "Sanat",
      MUZIK: "Müzik",
      BEDEN_EGITIMI: "Beden Eğitimi",
      DIGER: "Diğer",
    };
    return courseTypes[courseType] || courseType;
  };

  return (
    <div className="space-y-4">
      {/* Hero Header */}
      <div className="text-center space-y-4">
        <div>
          <Text
            variant="h1"
            className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2"
          >
            📋 UBD Planınızın Özeti
          </Text>
          <Text
            variant="p"
            className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto"
          >
            Oluşturduğunuz UBD planının detaylarını gözden geçirin ve ders
            akışınızı oluşturun.
          </Text>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="gap-6">
        {/* UBD Components Overview */}
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-purple-500 rounded-lg">
              <Target className="w-5 h-5 text-white" />
            </div>
            <Text
              variant="h3"
              className="font-semibold text-gray-900 dark:text-gray-100"
            >
              UBD Bileşenleri
            </Text>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex justify-between items-center">
              <Text
                variant="p"
                size="sm"
                className="text-gray-600 dark:text-gray-400"
              >
                Anlayış Hedefleri:
              </Text>
              <div className="flex items-center space-x-2">
                <Text
                  variant="p"
                  size="sm"
                  className="font-medium text-gray-900 dark:text-gray-100"
                >
                  {formData.ubdUnderstandingGoals?.length || 0} adet
                </Text>
                {(formData.ubdUnderstandingGoals?.length || 0) > 0 && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <Text
                variant="p"
                size="sm"
                className="text-gray-600 dark:text-gray-400"
              >
                Transfer Hedefleri:
              </Text>
              <div className="flex items-center space-x-2">
                <Text
                  variant="p"
                  size="sm"
                  className="font-medium text-gray-900 dark:text-gray-100"
                >
                  {formData.ubdTransferGoals?.length || 0} adet
                </Text>
                {(formData.ubdTransferGoals?.length || 0) > 0 && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <Text
                variant="p"
                size="sm"
                className="text-gray-600 dark:text-gray-400"
              >
                Temel Sorular:
              </Text>
              <div className="flex items-center space-x-2">
                <Text
                  variant="p"
                  size="sm"
                  className="font-medium text-gray-900 dark:text-gray-100"
                >
                  {formData.ubdEssentialQuestions?.length || 0} adet
                </Text>
                {(formData.ubdEssentialQuestions?.length || 0) > 0 && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <Text
                variant="p"
                size="sm"
                className="text-gray-600 dark:text-gray-400"
              >
                Değerlendirme Görevleri:
              </Text>
              <div className="flex items-center space-x-2">
                <Text
                  variant="p"
                  size="sm"
                  className="font-medium text-gray-900 dark:text-gray-100"
                >
                  {(formData.ubdPerformanceTasks?.length || 0) +
                    (formData.ubdOtherEvidence?.length || 0)}{" "}
                  adet
                </Text>
                {(formData.ubdPerformanceTasks?.length || 0) +
                  (formData.ubdOtherEvidence?.length || 0) >
                  0 && <CheckCircle className="w-4 h-4 text-green-500" />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Summary Sections */}
      <div className="space-y-6">
        {/* Understanding Goals */}
        {formData.ubdUnderstandingGoals &&
          formData.ubdUnderstandingGoals.length > 0 && (
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-indigo-500 rounded-lg">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <Text
                  variant="h3"
                  className="font-semibold text-gray-900 dark:text-gray-100"
                >
                  Anlayış Hedefleri
                </Text>
              </div>
              <div className="space-y-3">
                {formData.ubdUnderstandingGoals.map((goal, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                  >
                    <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Text
                        variant="p"
                        size="sm"
                        className="text-indigo-600 dark:text-indigo-400 font-medium"
                      >
                        {index + 1}
                      </Text>
                    </div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {goal}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Transfer Goals */}
        {formData.ubdTransferGoals && formData.ubdTransferGoals.length > 0 && (
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-teal-500 rounded-lg">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
              <Text
                variant="h3"
                className="font-semibold text-gray-900 dark:text-gray-100"
              >
                Transfer Hedefleri
              </Text>
            </div>
            <div className="space-y-3">
              {formData.ubdTransferGoals.map((goal, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                >
                  <div className="w-6 h-6 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Text
                      variant="p"
                      size="sm"
                      className="text-teal-600 dark:text-teal-400 font-medium"
                    >
                      {index + 1}
                    </Text>
                  </div>
                  <Text
                    variant="p"
                    size="sm"
                    className="text-gray-700 dark:text-gray-300"
                  >
                    {goal}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Essential Questions */}
        {formData.ubdEssentialQuestions &&
          formData.ubdEssentialQuestions.length > 0 && (
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <Text
                  variant="h3"
                  className="font-semibold text-gray-900 dark:text-gray-100"
                >
                  Temel Sorular
                </Text>
              </div>
              <div className="space-y-3">
                {formData.ubdEssentialQuestions.map((question, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                  >
                    <div className="w-6 h-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Text
                        variant="p"
                        size="sm"
                        className="text-amber-600 dark:text-amber-400 font-medium"
                      >
                        ?
                      </Text>
                    </div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {question}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Performance Tasks */}
        {formData.ubdPerformanceTasks &&
          formData.ubdPerformanceTasks.length > 0 && (
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-rose-500 rounded-lg">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <Text
                  variant="h3"
                  className="font-semibold text-gray-900 dark:text-gray-100"
                >
                  Performans Görevleri
                </Text>
              </div>
              <div className="space-y-3">
                {formData.ubdPerformanceTasks.map((task, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                  >
                    <div className="w-6 h-6 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <Text
                        variant="p"
                        size="sm"
                        className="text-rose-600 dark:text-rose-400 font-medium"
                      >
                        {index + 1}
                      </Text>
                    </div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-gray-700 dark:text-gray-300"
                    >
                      {task}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Other Evidence */}
        {formData.ubdOtherEvidence && formData.ubdOtherEvidence.length > 0 && (
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/30 dark:border-gray-700/30 shadow-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-purple-500 rounded-lg">
                <CheckSquare className="w-5 h-5 text-white" />
              </div>
              <Text
                variant="h3"
                className="font-semibold text-gray-900 dark:text-gray-100"
              >
                Diğer Kanıtlar
              </Text>
            </div>
            <div className="space-y-3">
              {formData.ubdOtherEvidence.map((evidence, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                >
                  <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <Text
                      variant="p"
                      size="sm"
                      className="text-purple-600 dark:text-purple-400 font-medium"
                    >
                      {index + 1}
                    </Text>
                  </div>
                  <Text
                    variant="p"
                    size="sm"
                    className="text-gray-700 dark:text-gray-300"
                  >
                    {evidence}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-2xl border border-teal-200 dark:border-teal-700/30 hover:shadow-md transition-shadow">
          <Heart className="w-8 h-8 text-teal-600 dark:text-teal-400 mx-auto mb-3" />
          <Text
            variant="h4"
            className="font-semibold text-teal-700 dark:text-teal-300 mb-2"
          >
            Hedef Odaklı Tasarım
          </Text>
          <Text
            variant="p"
            size="sm"
            className="text-teal-600 dark:text-teal-400"
          >
            UBD hedeflerinize uygun özel aktiviteler
          </Text>
        </div>
        <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-2xl border border-teal-200 dark:border-teal-700/30 hover:shadow-md transition-shadow">
          <Clock className="w-8 h-8 text-teal-600 dark:text-teal-400 mx-auto mb-3" />
          <Text
            variant="h4"
            className="font-semibold text-teal-700 dark:text-teal-300 mb-2"
          >
            Akıllı Zamanlama
          </Text>
          <Text
            variant="p"
            size="sm"
            className="text-teal-600 dark:text-teal-400"
          >
            Optimum ders süresi ve etkinlik dağılımı
          </Text>
        </div>
        <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-2xl border border-teal-200 dark:border-teal-700/30 hover:shadow-md transition-shadow">
          <Star className="w-8 h-8 text-teal-600 dark:text-teal-400 mx-auto mb-3" />
          <Text
            variant="h4"
            className="font-semibold text-teal-700 dark:text-teal-300 mb-2"
          >
            Kişiselleştirme
          </Text>
          <Text
            variant="p"
            size="sm"
            className="text-teal-600 dark:text-teal-400"
          >
            Sınıf seviyesi ve öğrenci ihtiyaçları
          </Text>
        </div>
      </div>

      {/* Action Button */}
      <div className="text-center space-y-4">
        <Button
          variant="solid"
          onClick={onCreateLessonFlow}
          disabled={isLoading || completionPercentage < 30 || !formData.planId}
          className="px-12 py-4 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 text-lg transform hover:scale-105"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mr-3"></div>
              YZ Ders Akışı Oluşturuyor...
            </>
          ) : (
            <>
              <Sparkles className="w-6 h-6 mr-3" />
              YZ ile Ders Akışı Oluştur
              <ArrowRight className="w-6 h-6 ml-3" />
            </>
          )}
        </Button>

        {(completionPercentage < 30 || !formData.planId) && (
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-full border border-amber-200 dark:border-amber-700/30">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <Text
              variant="p"
              size="sm"
              className="text-amber-600 dark:text-amber-400"
            >
              {!formData.planId
                ? "Plan ID gerekli - lütfen önce planı kaydedin"
                : "Kaliteli bir ders akışı için daha fazla UBD bileşeni gerekli (en az %30)"}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default UbdPlanSummaryStep;
